import type { Patch } from "../engine/types";
import type { Palette, PatchSlot } from "./palette";
import { colorFor, genId, savePalette } from "./palette";
import { clonePatch } from "../engine/defaultPatch";

// A ready-made starter kit showing off the engine's range (noise, pitch-env,
// drive, FM). Seeded once into empty clipboard slots so a new user — and Victor
// on first load after this ships — has sounds to play with immediately. Guarded
// by a localStorage flag so deleting a kit tile is permanent (never re-added).

interface Spec {
  oscillator?: Partial<Patch["oscillator"]>;
  filter?: Partial<Patch["filter"]>;
  ampEnv?: Partial<Patch["ampEnv"]>;
  lfo?: Partial<Patch["lfo"]>;
  masterGain?: number;
  drive?: number;
  pitchEnv?: Patch["pitchEnv"];
  fm?: Patch["fm"];
  filterEnv?: Patch["filterEnv"];
}

function mk(name: string, o: Spec): Patch {
  const p: Patch = {
    version: 1,
    name,
    oscillator: { type: "sine", detune: 0, pulseWidth: 0.5, ...o.oscillator },
    filter: { type: "lowpass", cutoff: 3000, q: 1, ...o.filter },
    ampEnv: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.1, ...o.ampEnv },
    lfo: { target: "off", shape: "sine", rate: 5, depth: 0, ...o.lfo },
    masterGain: o.masterGain ?? 0.8,
  };
  if (o.drive !== undefined) p.drive = o.drive;
  if (o.pitchEnv) p.pitchEnv = o.pitchEnv;
  if (o.fm) p.fm = o.fm;
  if (o.filterEnv) p.filterEnv = o.filterEnv;
  return p;
}

interface KitItem {
  name: string;
  midi: number;
  patch: Patch;
  /** Kit version this preset was introduced in (so migrations only ADD genuinely
   *  new presets, never re-add ones a user deleted). Defaults to 1. */
  since?: number;
}

const KIT: KitItem[] = [
  // A clean starting point.
  { name: "Sine", midi: 60, since: 4, patch: mk("Sine", {
    oscillator: { type: "sine" }, filter: { type: "lowpass", cutoff: 8000, q: 0.7 },
    ampEnv: { attack: 0.01, decay: 0.2, sustain: 0.8, release: 0.3 }, masterGain: 0.5 }) },
  // --- Instruments (synth approximations) ---
  { name: "Guitar", midi: 52, since: 4, patch: mk("Guitar", {
    oscillator: { type: "sawtooth" }, filter: { type: "lowpass", cutoff: 1600, q: 2 },
    filterEnv: { amount: 4200, decay: 0.28 },
    ampEnv: { attack: 0.003, decay: 0.5, sustain: 0, release: 0.3 },
    drive: 0.12, masterGain: 0.6 }) },
  { name: "Guitar 2", midi: 52, since: 5, patch: mk("Guitar 2", {
    // A second take — hollow pulse body + a steel-string shimmer + a bit of bite.
    oscillator: { type: "pulse", pulseWidth: 0.4 }, fm: { ratio: 1, depth: 0.5 },
    filter: { type: "lowpass", cutoff: 2200, q: 1.5 }, filterEnv: { amount: 3000, decay: 0.35 },
    ampEnv: { attack: 0.004, decay: 0.7, sustain: 0.05, release: 0.4 },
    drive: 0.2, masterGain: 0.55 }) },
  { name: "Violin", midi: 67, since: 4, patch: mk("Violin", {
    // Warmer body + slightly quicker bow-start, gentler vibrato.
    oscillator: { type: "sawtooth" }, filter: { type: "lowpass", cutoff: 2600, q: 1.2 },
    ampEnv: { attack: 0.12, decay: 0.2, sustain: 0.82, release: 0.3 },
    lfo: { target: "pitch", shape: "sine", rate: 5.5, depth: 5 }, masterGain: 0.55 }) },
  { name: "Guqin", midi: 57, since: 4, patch: mk("Guqin", {
    // Chinese plucked zither — soft triangle + gentle harmonic, long resonant decay.
    oscillator: { type: "triangle" }, fm: { ratio: 3, depth: 0.7 },
    filter: { type: "lowpass", cutoff: 2800, q: 1 },
    ampEnv: { attack: 0.004, decay: 0.9, sustain: 0, release: 0.7 }, masterGain: 0.55 }) },
  { name: "Mandolin", midi: 64, since: 4, patch: mk("Mandolin", {
    // Double-course feel: two strings beat against each other (detune) + the
    // signature fast tremolo (amplitude LFO).
    oscillator: { type: "sawtooth", detune: 14 }, filter: { type: "lowpass", cutoff: 3500, q: 1.5 },
    ampEnv: { attack: 0.003, decay: 0.5, sustain: 0, release: 0.3 },
    lfo: { target: "amplitude", shape: "sine", rate: 9, depth: 0.18 }, masterGain: 0.55 }) },
  { name: "Piano", midi: 60, since: 4, patch: mk("Piano", {
    // Softer FM (less electric), longer natural decay.
    oscillator: { type: "sine" }, fm: { ratio: 4, depth: 2.2 },
    filter: { type: "lowpass", cutoff: 5500, q: 0.8 }, filterEnv: { amount: 3500, decay: 0.5 },
    ampEnv: { attack: 0.002, decay: 1.2, sustain: 0.12, release: 0.4 }, masterGain: 0.55 }) },
  { name: "Organ", midi: 60, since: 4, patch: mk("Organ", {
    oscillator: { type: "sine" }, fm: { ratio: 2, depth: 4 },
    filter: { type: "lowpass", cutoff: 4500, q: 0.8 },
    ampEnv: { attack: 0.01, decay: 0.05, sustain: 1.0, release: 0.12 },
    lfo: { target: "amplitude", shape: "sine", rate: 6, depth: 0.12 }, masterGain: 0.5 }) },
  // --- Drums / percussion ---
  { name: "Kick", midi: 36, patch: mk("Kick", {
    oscillator: { type: "sine" }, filter: { type: "lowpass", cutoff: 2200, q: 1 },
    ampEnv: { attack: 0.001, decay: 0.18, sustain: 0, release: 0.08 },
    pitchEnv: { amount: 36, decay: 0.06 }, drive: 0.2, masterGain: 0.95 }) },
  { name: "Snare", midi: 60, patch: mk("Snare", {
    oscillator: { type: "noise" }, filter: { type: "bandpass", cutoff: 1800, q: 4 },
    ampEnv: { attack: 0.001, decay: 0.18, sustain: 0, release: 0.05 }, masterGain: 0.8 }) },
  { name: "Hi-hat", midi: 60, patch: mk("Hi-hat", {
    oscillator: { type: "noise" }, filter: { type: "highpass", cutoff: 7000, q: 1 },
    ampEnv: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.03 }, masterGain: 0.6 }) },
  { name: "Tom", midi: 48, patch: mk("Tom", {
    oscillator: { type: "triangle" }, filter: { type: "lowpass", cutoff: 1400, q: 1 },
    ampEnv: { attack: 0.001, decay: 0.22, sustain: 0, release: 0.1 },
    pitchEnv: { amount: 18, decay: 0.12 }, masterGain: 0.9 }) },
  { name: "Clap", midi: 60, patch: mk("Clap", {
    // Fast square amp-flutter over the noise burst ≈ the multi-hit clap texture.
    oscillator: { type: "noise" }, filter: { type: "bandpass", cutoff: 1500, q: 2 },
    ampEnv: { attack: 0.001, decay: 0.16, sustain: 0, release: 0.08 },
    lfo: { target: "amplitude", shape: "square", rate: 27, depth: 0.55 }, masterGain: 0.7 }) },
  { name: "Metal", midi: 48, since: 5, patch: mk("Metal", {
    // New take — a metallic drum + bell hit: inharmonic FM ring (bell) over a
    // fast pitch drop (drum thump), percussive envelope.
    oscillator: { type: "sine" }, fm: { ratio: 5.4, depth: 7 },
    filter: { type: "lowpass", cutoff: 6000, q: 1 },
    ampEnv: { attack: 0.001, decay: 0.35, sustain: 0, release: 0.25 },
    pitchEnv: { amount: 10, decay: 0.05 }, drive: 0.2, masterGain: 0.6 }) },
  { name: "SciFi", midi: 60, patch: mk("SciFi", {
    // The old Metal sound, renamed — FM metallic lead, sustained + a touch of grit.
    oscillator: { type: "sine" }, fm: { ratio: 3.0, depth: 2.4 },
    filter: { type: "lowpass", cutoff: 5200, q: 1.4 },
    ampEnv: { attack: 0.004, decay: 0.28, sustain: 0.42, release: 0.28 },
    drive: 0.18, masterGain: 0.6 }) },
  { name: "Bell", midi: 72, patch: mk("Bell", {
    oscillator: { type: "sine" }, ampEnv: { attack: 0.001, decay: 0.6, sustain: 0, release: 0.5 },
    fm: { ratio: 3.5, depth: 4 }, masterGain: 0.7 }) },
  { name: "Clang", midi: 60, patch: mk("Clang", {
    oscillator: { type: "sine" }, ampEnv: { attack: 0.001, decay: 0.28, sustain: 0, release: 0.2 },
    fm: { ratio: 5.1, depth: 8 }, drive: 0.2, masterGain: 0.6 }) },
];

const KIT_VERSION = 6;            // bump when a factory preset changes / is added
const VER_KEY = "wap.kitVersion";
const LEGACY_FLAG = "wap.kitSeeded"; // pre-versioning "already seeded" marker

// Factory presets renamed across versions: an existing factory tile with the
// old name is renamed (then revoiced by the per-item loop) so users don't end
// up with a stale duplicate.
const RENAMES: Record<string, string> = { Erhu: "Guqin", Lyre: "Mandolin", Metal: "SciFi" };

function slotFor(item: KitItem): PatchSlot {
  return {
    id: genId(),
    kind: "patch",
    name: item.name,
    color: colorFor(item.patch),
    patch: clonePatch(item.patch),
    lastMidi: item.midi,
    preset: true,
  };
}

/**
 * First run ever → fill empty palette slots with the starter kit. On a later
 * KIT_VERSION bump → refresh the factory tiles in place (by name) so improved
 * presets (e.g. Metal) reach existing users without touching positions or other
 * slots. Returns true if anything changed.
 */
export function seedKitIfFirstRun(palette: Palette): boolean {
  let version = 0;
  try {
    const stored = localStorage.getItem(VER_KEY);
    if (stored) version = Number(stored) || 0;
    else if (localStorage.getItem(LEGACY_FLAG)) version = 1; // seeded before versioning
  } catch {
    return false;
  }

  let changed = false;
  if (version === 0) {
    // Never seeded — drop the kit into the first empty slots.
    for (const item of KIT) {
      const i = palette.findIndex((s) => s === null);
      if (i < 0) break;
      palette[i] = slotFor(item);
      changed = true;
    }
  } else if (version < KIT_VERSION) {
    // Rename factory tiles first (only factory ones), so the per-item loop then
    // finds + revoices them under the new name.
    for (const slot of palette) {
      if (slot && slot.kind === "patch" && slot.preset && RENAMES[slot.name]) {
        slot.name = RENAMES[slot.name];
        changed = true;
      }
    }
    for (const item of KIT) {
      const slot = palette.find(
        (s): s is PatchSlot => !!s && s.kind === "patch" && s.name === item.name,
      );
      if (slot) {
        // Update an existing factory tile in place to the latest sound.
        slot.patch = clonePatch(item.patch);
        slot.lastMidi = item.midi;
        slot.color = colorFor(item.patch);
        slot.preset = true;
        changed = true;
      } else if ((item.since ?? 1) > version) {
        // A preset introduced *after* the user's version → add to an empty slot.
        // (Older presets that aren't present were deleted on purpose — leave them.)
        const i = palette.findIndex((s) => s === null);
        if (i >= 0) {
          palette[i] = slotFor(item);
          changed = true;
        }
      }
    }
  }

  try { localStorage.setItem(VER_KEY, String(KIT_VERSION)); } catch { /* ignore */ }
  if (changed) savePalette(palette);
  return changed;
}
